// src/context/SchoolProvider.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { getAuth } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";

const SchoolCtx = createContext({ school: null, schoolId: null, role: null, loading: true });
export const useSchool = () => useContext(SchoolCtx);

async function discoverSchoolId(user) {
  if (!user) return null;

  // 1) custom claims?
  try {
    const token = await getIdTokenResult(user, true);
    const claimSchool = token.claims?.schoolId || token.claims?.sid;
    if (claimSchool) return String(claimSchool);
  } catch {}

  // 2) cached?
  const cached = localStorage.getItem("schoolId");
  if (cached) return cached;

  // 3) look up by common profile locations (student/teacher/admin)
  const candidates = [
    doc(db, "students", user.uid),                                     // if you keep a global students collection
    doc(db, "teachers", user.uid),
    doc(db, "users", user.uid),
    // if you only keep nested docs, comment these & use your own locators
  ];

  for (const ref of candidates) {
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const sid = snap.data()?.schoolId;
        if (sid) return String(sid);
      }
    } catch {}
  }

  // 4) nested (recommended): try each role under schools/* collections
  // NOTE: replace role detection the way you store users.
  const roleLocators = [
    { role: "student", coll: "students" },
    { role: "teacher", coll: "teachers" },
    { role: "admin",   coll: "admins"   },
  ];
  for (const { coll } of roleLocators) {
    // If you saved studentId/teacherId/schoolId in localStorage during login, use it:
    const sid = localStorage.getItem("schoolId");
    if (sid) return sid;
  }

  return null;
}

export default function SchoolProvider({ children }) {
  const [schoolId, setSchoolId] = useState(localStorage.getItem("schoolId") || null);
  const [school, setSchool] = useState(null);
  const [role, setRole] = useState(localStorage.getItem("role") || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSchoolId(null);
        setSchool(null);
        setRole(null);
        setLoading(false);
        return;
      }
      const sid = await discoverSchoolId(user);
      if (sid) {
        setSchoolId(sid);
        localStorage.setItem("schoolId", sid);
      }
      setLoading(false);
    });
    return () => unsubAuth();
  }, []);

  // live subscribe to the school doc
  useEffect(() => {
    if (!schoolId) { setSchool(null); return; }
    const ref = doc(db, "schools", schoolId);
    const unsub = onSnapshot(ref, (snap) => {
      setSchool(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [schoolId]);

  const value = useMemo(() => ({ school, schoolId, role, loading }), [school, schoolId, role, loading]);

  return <SchoolCtx.Provider value={value}>{children}</SchoolCtx.Provider>;
}
